import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SlideEditor, { SlideContent } from './SlideEditor';
import * as PresentationServiceModule from '../services/PresentationService';
import claudeService from '../services/ClaudeService';
import { FileWithContent } from '../services/PresentationService';
import slidePlanService, { PresentationOutline } from '../services/SlidePlanService';
import SlidePlanEditor from './SlidePlanEditor';

const presentationService = PresentationServiceModule.default;
type Presentation = PresentationServiceModule.Presentation;

const PresentationEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [presentation, setPresentation] = useState<Presentation | null>(null);
  const [slideHtml, setSlideHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isPlanningSlides, setIsPlanningSlides] = useState(false);
  const [slidePlan, setSlidePlan] = useState<PresentationOutline | null>(null);
  const [isShowingStreaming, setIsShowingStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingProgress, setStreamingProgress] = useState(0);

  useEffect(() => {
    const fetchPresentation = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await presentationService.getPresentation(id);
        if (data) {
          setPresentation(data);
          setSlideHtml(data.html);
          
          // If the presentation has a saved slide plan, load it
          if (data.slidePlan) {
            setSlidePlan(data.slidePlan);
          }
          
          setError(null);
        } else {
          setError('Presentation not found');
        }
      } catch (err) {
        console.error('Error fetching presentation:', err);
        setError('Failed to load presentation. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPresentation();
  }, [id]);

  const handleGenerateSlides = async (content: SlideContent, contextFiles: FileWithContent[]) => {
    try {
      setSaving(true);
      setSuccess(null);
      setError(null);
      
      // Call Claude API to generate slides
      const html = await claudeService.generateSlides(content, contextFiles);
      setSlideHtml(html);
      
      // If editing an existing presentation, update it
      if (id && presentation) {
        await presentationService.updatePresentation(id, {
          title: content.title,
          content: content.content,
          html,
          contextFiles: (presentation.contextFiles || []).map(file => ({
            id: file.id || '',
            name: file.name,
            type: file.type,
            size: file.size,
            content: file.content || '',
            storageURL: file.storageURL || '',
            base64Data: file.base64Data,
            summary: file.summary,
            tokenCount: file.tokenCount
          }))
        });
        setSuccess('Presentation updated successfully');
      }
    } catch (error) {
      console.error('Error generating or saving slides:', error);
      setError('There was an error updating your presentation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSlidePlan = async () => {
    if (!presentation || !presentation.content) {
      setError('Please provide content for your presentation.');
      return;
    }

    // Reset streaming state and show UI
    setStreamingContent('');
    setStreamingProgress(0);
    setIsShowingStreaming(true);
    setStatus('Initializing plan generation...');
    
    try {
      const slideContent: SlideContent = {
        title: presentation.title || '',
        content: presentation.content || ''
      };
      
      // Track when last update was made to avoid too frequent UI updates
      let lastUpdateTime = Date.now();
      const updateThrottleMs = 50; // Throttle updates for smoother rendering
      
      console.log('Starting slide plan generation with slidePlanService.generateSlidePlan');
      
      const outline = await slidePlanService.generateSlidePlan(
        slideContent,
        presentation.contextFiles || [],
        {
          onThinking: (thinking) => {
            // Throttle updates to prevent UI jank
            const now = Date.now();
            if (now - lastUpdateTime > updateThrottleMs) {
              setStreamingContent(thinking);
              
              // Estimate progress based on thinking length to provide visual feedback
              const thinkingLength = thinking.length;
              let progress = 5;
              if (thinkingLength > 100) progress = 15;
              if (thinkingLength > 300) progress = 25;
              if (thinkingLength > 600) progress = 40;
              if (thinkingLength > 1000) progress = 55;
              if (thinkingLength > 1500) progress = 65;
              if (thinkingLength > 2000) progress = 75;
              
              setStreamingProgress(progress);
              
              // Extract the most recent part of the thinking for the status
              const recentThinking = thinking.split('\n').slice(-2).join('\n');
              setStatus(`Planning: ${recentThinking}`);
              
              lastUpdateTime = now;
            }
          },
          onToolCall: (toolCall) => {
            console.log('Tool call received in UI handler:', toolCall);
            
            if (toolCall?.name === 'create_slide_outline' && toolCall.input) {
              setStreamingProgress(90);
              setSlidePlan(toolCall.input);
              setStatus('Finalizing slide plan structure...');
              
              // Display the JSON structure of the slide plan in a readable format
              setStreamingContent(prev => 
                prev + "\n\n✓ Generated slide plan structure:\n```json\n" + 
                JSON.stringify(toolCall.input, null, 2) + 
                "\n```"
              );
            }
          },
          onError: (error) => {
            console.error('Slide plan generation error:', error);
            setError(`Error: ${error}`);
            setIsShowingStreaming(false);
          },
          onComplete: (completed) => {
            console.log('Slide plan generation completed with outline:', completed);
            
            // Show 100% completion
            setStreamingProgress(100);
            setSlidePlan(completed);
            setStreamingContent(prev => prev + "\n\n✅ Slide plan generation complete!");
            setStatus('Slide plan generated! Opening editor for review and customization.');
            
            // Save the slide plan to Firestore
            if (id) {
              try {
                presentationService.updateSlidePlan(id, completed)
                  .then(() => {
                    console.log('Slide plan saved to Firestore');
                    // Show the slide plan editor after a brief pause so user can see completion
                    setTimeout(() => {
                      setIsShowingStreaming(false);
                      setIsPlanningSlides(true);
                    }, 1500);
                  })
                  .catch(error => {
                    console.error('Error saving slide plan to Firestore:', error);
                    setIsShowingStreaming(false);
                    setIsPlanningSlides(true);
                  });
              } catch (saveError) {
                console.error('Error saving slide plan to Firestore:', saveError);
                setIsShowingStreaming(false);
                setIsPlanningSlides(true);
              }
            } else {
              // If no ID, just show the editor after a brief pause
              setTimeout(() => {
                setIsShowingStreaming(false);
                setIsPlanningSlides(true);
              }, 1500);
            }
          }
        }
      );
      
      // This might be reached if the outline is returned directly (not via callback)
      if (!slidePlan && outline) {
        console.log('Outline returned directly from generateSlidePlan:', outline);
        setSlidePlan(outline);
        
        // Save the slide plan to Firestore
        if (id) {
          try {
            await presentationService.updateSlidePlan(id, outline);
            console.log('Slide plan saved to Firestore');
          } catch (saveError) {
            console.error('Error saving slide plan to Firestore:', saveError);
          }
        }
        
        setIsPlanningSlides(true);
      }
    } catch (error) {
      console.error('Error generating slide plan:', error);
      setError('Failed to generate slide plan. Please try again.');
      setStatus('Failed to generate slide plan. Please try again.');
      setIsShowingStreaming(false);
    }
  };

  const handleGenerateFromPlan = async (outline: PresentationOutline): Promise<string> => {
    setGenerationLoading(true);
    setError(null);
    
    // Save the updated slide plan first
    if (id) {
      try {
        await presentationService.updateSlidePlan(id, outline);
        console.log('Updated slide plan saved to Firestore');
      } catch (error) {
        console.error('Error saving updated slide plan to Firestore:', error);
        // Don't throw this error as it's not critical to the UX flow
      }
    }
    
    // Create a detailed slide content from the outline
    const enhancedContent: SlideContent = {
      title: outline.presentationTitle,
      content: `${outline.presentationSummary}\n\n${outline.slides.map(slide => 
        `Slide ${slide.slideNumber} (${slide.slideType}): ${slide.title}\n${slide.content}\n${slide.notes ? `Notes: ${slide.notes}` : ''}`
      ).join('\n\n')}`
    };
    
    try {
      if (!presentation) {
        throw new Error('Presentation data not available');
      }
      
      const html = await claudeService.generateSlides(
        enhancedContent,
        presentation.contextFiles || [],
        {
          onThinking: (thinking) => {
            setStatus(`Thinking: ${thinking.substring(thinking.length - 100)}`);
          },
          onPartialResponse: (text) => {
            setStatus(`Generating slides${'.'.repeat(Math.floor(text.length / 100) % 4)}`);
          },
          onComplete: (html) => {
            setSlideHtml(html);
            setStatus('Slides generated successfully!');
            // Save the presentation with the generated HTML and the slide plan
            if (id && presentation) {
              presentationService.updatePresentation(id, {
                title: presentation.title,
                content: presentation.content,
                html,
                slidePlan: outline, // Include the slide plan in the update
                contextFiles: (presentation.contextFiles || []).map(file => ({
                  id: file.id || '',
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  content: file.content || '',
                  storageURL: file.storageURL || '',
                  base64Data: file.base64Data,
                  summary: file.summary,
                  tokenCount: file.tokenCount
                }))
              });
            }
          }
        }
      );
      
      // Close planning mode after successful generation
      setIsPlanningSlides(false);
      
      return html;
      
    } catch (error) {
      console.error('Error generating slides:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate slides');
      throw error;
    } finally {
      setGenerationLoading(false);
    }
  };

  const handleDeploy = () => {
    // Implementation for deploying the presentation
  };

  const handlePreview = () => {
    // Implementation for previewing the presentation
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-64">
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-violet-600 to-teal-600 rounded-full opacity-70 blur-lg animate-pulse"></div>
          <div className="relative w-12 h-12 border-4 border-t-transparent border-teal-400 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || !presentation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-300">
          {error || 'Presentation not found'}
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mr-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                {presentation?.title || 'New Presentation'}
                {status === 'saved' && (
                  <span className="ml-2 flex items-center text-sm text-green-600 dark:text-green-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Saved
                  </span>
                )}
              </h1>
              {presentation?.updatedAt && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Last edited: {
                    // Handle different possible types of updatedAt
                    typeof presentation.updatedAt === 'object' && 'toDate' in presentation.updatedAt
                      ? presentation.updatedAt.toDate().toLocaleString()
                      : new Date(presentation.updatedAt as any).toLocaleString()
                  }
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleGenerateSlidePlan}
              disabled={!presentation?.content}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                !presentation?.content
                ? 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
              }`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
              Plan Slides
            </button>
            <button
              onClick={handleDeploy}
              disabled={!slideHtml || saving}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                !slideHtml || saving 
                ? 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white shadow-sm'
              }`}
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Publishing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                  </svg>
                  Publish
                </>
              )}
            </button>
            {slideHtml && (
              <button
                onClick={handlePreview}
                className="px-4 py-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-800/50 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                Present
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Alerts */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 mx-auto max-w-7xl mt-4 rounded-md" role="alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <p>{error}</p>
            </div>
            <button 
              onClick={() => setError(null)} 
              className="ml-auto pl-3"
            >
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 mx-auto max-w-7xl mt-4 rounded-md" role="alert">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-3">
              <p>{success}</p>
            </div>
            <button 
              onClick={() => setSuccess(null)} 
              className="ml-auto pl-3"
            >
              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-[80vh]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-l-2 border-blue-600 dark:border-blue-500"></div>
            <p className="mt-3 text-gray-600 dark:text-gray-400">Loading presentation...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <div className={`space-y-8 ${slideHtml ? 'lg:grid lg:grid-cols-2 lg:gap-8 lg:space-y-0' : ''}`}>
              <div className="lg:col-span-1">
                <SlideEditor
                  onGenerateSlides={handleGenerateSlides}
                  isLoading={generationLoading}
                  initialTitle={presentation?.title || ''}
                  initialContent={presentation?.content || ''}
                  initialContextFiles={presentation?.contextFiles || []}
                  submitButtonText={slideHtml ? 'Regenerate Slides' : 'Generate Slides'}
                  presentationId={id}
                />
              </div>
              
              {slideHtml && (
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-900 shadow rounded-lg overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Preview</h2>
                      <button
                        onClick={handlePreview}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        Full Screen
                      </button>
                    </div>
                    <div className="aspect-[16/9] bg-white overflow-hidden">
                      <iframe 
                        srcDoc={slideHtml}
                        title="Presentation Preview"
                        className="w-full h-full border-0"
                        data-testid="presentation-preview"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* SlidePlanEditor Modal */}
      {isPlanningSlides && slidePlan && (
        <div className="fixed inset-0 bg-gray-900/80 z-[60] overflow-y-auto flex items-center justify-center backdrop-blur-sm">
          <div className="w-full max-w-6xl mx-auto p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-800">
              <SlidePlanEditor
                outline={slidePlan}
                onSave={(outline) => {
                  setSlidePlan(outline);
                  if (id) {
                    try {
                      presentationService.updateSlidePlan(id, outline)
                        .catch(error => console.error('Error saving slide plan update:', error));
                    } catch (error) {
                      console.error('Error saving slide plan update:', error);
                    }
                  }
                }}
                onGenerate={handleGenerateFromPlan}
                onCancel={() => setIsPlanningSlides(false)}
                isLoadedFromFirestore={!!presentation?.slidePlan}
              />
            </div>
          </div>
        </div>
      )}

      {/* Streaming UI Modal */}
      {isShowingStreaming && (
        <div className="fixed inset-0 bg-gray-900/80 z-[60] overflow-y-auto flex items-center justify-center backdrop-blur-sm">
          <div className="w-full max-w-4xl mx-auto p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full mr-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Creating Slide Plan</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    {streamingProgress < 30 ? 'Analyzing content and structuring ideas...' :
                     streamingProgress < 60 ? 'Planning slide structure and organization...' :
                     streamingProgress < 90 ? 'Developing presentation outline...' :
                     'Finalizing slide plan...'}
                  </p>
                </div>
                {streamingProgress >= 100 && (
                  <div className="flex-shrink-0 ml-2">
                    <div className="text-green-500 flex items-center">
                      <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-sm font-medium">Complete</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                  <span>{streamingProgress < 100 ? 'Generating plan...' : 'Plan completed!'}</span>
                  <span>{streamingProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${streamingProgress === 100 ? 'bg-green-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                    style={{ width: `${streamingProgress}%`, transition: 'width 0.3s ease' }}
                  ></div>
                </div>
              </div>
              
              {/* Thinking content */}
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 h-[400px] overflow-y-auto">
                <div className="flex items-center mb-2">
                  <div className={`w-4 h-4 ${streamingProgress < 100 ? 'bg-blue-600 animate-pulse' : 'bg-green-500'} rounded-full mr-2`}></div>
                  <h4 className="text-blue-700 dark:text-blue-400 font-semibold">
                    {streamingProgress < 100 ? 'AI Analysis in Progress' : 'Analysis Complete'}
                  </h4>
                </div>
                <div 
                  className="whitespace-pre-wrap font-mono text-xs text-gray-700 dark:text-gray-300 overflow-auto"
                  style={{ 
                    maxHeight: '350px',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth'
                  }}
                >
                  {streamingContent || 'Initializing Claude...\nAnalyzing presentation requirements...'}
                </div>
              </div>

              {/* Message about what's happening */}
              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Claude is creating a structured plan for your presentation that you can review and customize before generating the final slides.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationEditor; 